import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import i18n from "../../../../utils/i18n";

/**
 * Schema of variables collected during the search flow.
 * Each entry: { key, label, step, possibleValues, type }
 *   - key: dot-path inside company_store (e.g. "industryspecifics.general.simplifiedpayroll")
 *   - possibleValues: human readable description of allowed values (info only)
 *   - type: 'string' | 'boolean' | 'number' | 'object' | 'array'
 *
 * Order roughly follows the SEARCHFLOW_VARIABLES.md document.
 */
const SEARCH_VARIABLES = [
  // Step 0 - needs.jsx
  {
    key: "answer",
    step: 0,
    type: "string",
    possibleValues: "'existing' | 'founding' | 'questions'",
  },
  {
    key: "multiplecompany",
    step: 0,
    type: "string|boolean",
    possibleValues: "true / false (string is lehet)",
  },
  {
    key: "accounting",
    step: 0,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "newlyfounded",
    step: 0,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "legalentityfounding",
    step: 0,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "taxconsultingcomplex",
    step: 0,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "taxconsultingsimple",
    step: 0,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "companyctcname",
    step: 0,
    type: "string",
    recommended: true,
    possibleValues: "string (cég/személy neve)",
  },
  {
    key: "question",
    step: 0,
    type: "string",
    possibleValues: "string (felhasználó kérdése)",
  },

  // Step 1 - companyType.jsx
  {
    key: "companytype",
    step: 1,
    type: "string",
    required: true,
    possibleValues:
      "'bt' | 'kft' | 'zrt' | 'kiva' | 'singleprimary' | 'singlesecondary' | 'vszja' | 'entrepreneur' | 'idk'",
  },
  {
    key: "reqFromBupa",
    step: 1,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "isCallBackReq",
    step: 1,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "callbacksource",
    step: 1,
    type: "string",
    possibleValues: "pl. 'regkftrequest'",
  },
  { key: "leadtype", step: 1, type: "string", possibleValues: "pl. 'REG'" },

  // Step 2 - taxform.jsx
  {
    key: "taxform",
    step: 2,
    type: "string",
    required: true,
    possibleValues: "'kiva' | 'tao' | 'idk' | '' → companytype-től függően, entrepreneur esetén a companytype-ot állítja visszamenőlegesen és a taxform üres string",
  },

  // Step 3 - vat.jsx
  {
    key: "vatFilter",
    step: 3,
    type: "string",
    required: true,
    possibleValues: "'true' | 'false' | ''",
  },
  {
    key: "vatPayment",
    step: 3,
    type: "string",
    recommended: true,
    possibleValues: "'vatpayment1' | 'vatpayment2' | 'vatpayment3' | 'idk'",
  },

  // Step 4 - industry.jsx
  {
    key: "industry",
    step: 4,
    type: "string",
    required: true,
    possibleValues:
      "'vehicle' | 'etrade' | 'construction' | 'agriculture' | 'IT' | 'art' | 'beauty' | 'travel' | 'retail' | 'wholesale' | 'finance' | 'medicine' | 'courier' | 'engineer' | 'logistics' | 'manufacturing' | 'education' | 'startup' | 'realestate' | 'lawyer' | 'sport' | 'consultancy' | 'otherservices' | 'other'",
  },
  {
    key: "industryinput",
    step: 4,
    type: "string",
    possibleValues: "string (csak ha industry === 'other')",
  },

  // Step 5 - industrySpecific.jsx (whole nested object)
  {
    key: "industryspecifics",
    step: 5,
    type: "object",
    recommended: true,
    possibleValues:
      "Iparágonkénti beágyazott objektum (vehicle, etrade, construction, agriculture, IT, art, beauty, travel, retail, wholesale, finance, medicine, courier...)",
  },

  // Step 6 - banks.jsx
  {
    key: "bankingpref",
    step: 6,
    type: "object",
    recommended: true,
    possibleValues:
      "{ opt1..opt7: boolean, bankidk: boolean, other: boolean, bankingOtherInput: string }",
  },

  // Step 7 - accounts.jsx
  {
    key: "invoicingpref",
    step: 7,
    type: "object",
    recommended: true,
    possibleValues:
      "{ opt1..opt6: boolean, invoicingidk: boolean, other: boolean, invoicingOtherInput: string }",
  },

  // Step 8 - software.jsx
  {
    key: "softwarepref",
    step: 8,
    type: "object",
    recommended: true,
    possibleValues: "{ opt1..opt20: boolean, other: boolean }",
  },

  // Step 9 - foreignTrade.jsx
  {
    key: "foreign_trade",
    step: 9,
    type: "boolean",
    recommended: true,
    possibleValues: "true / false",
  },
  {
    key: "foreigntradeEU",
    step: 9,
    type: "string",
    possibleValues: "'true' | 'false' | ''",
  },

  // Step 10 - income.jsx
  {
    key: "yearlyrevenue",
    step: 10,
    type: "number",
    recommended: true,
    possibleValues: "5 | 10 | 50 | 200 | 500 | 1999 | 2000 | -10 (idk)",
  },
  {
    key: "noofemployees",
    step: 10,
    type: "number|string",
    recommended: true,
    possibleValues: "0–5 szám vagy '5+'",
  },
  {
    key: "payroll",
    step: 10,
    type: "boolean",
    recommended: true,
    possibleValues: "true / false (számított)",
  },

  // Step 11/12 - invoices
  {
    key: "incominginvoices",
    step: 11,
    type: "string",
    required: true,
    possibleValues:
      "'idk' | '0' | '10' | '20' | '50' | '100' | '200' | '500' | '1000' | '2000'",
  },
  {
    key: "outgoinginvoices",
    step: 12,
    type: "string",
    required: true,
    possibleValues:
      "'idk' | '0' | '10' | '20' | '50' | '100' | '200' | '500' | '1000' | '2000'",
  },

  // Step 13 - industrySpecificGeneral.jsx (general subobject)
  {
    key: "industryspecifics.general.simplifiedpayroll",
    step: 13,
    type: "string",
    possibleValues: "'true' | 'false' | 'idk' | ''",
  },
  {
    key: "industryspecifics.general.simplifiedemployeecount",
    step: 13,
    type: "number|string",
    possibleValues: "szám vagy ''",
  },
  {
    key: "industryspecifics.general.severallocations",
    step: 13,
    type: "object",
    possibleValues: "{ status: 'true'|'false'|'', locationcount: number|null }",
  },
  {
    key: "industryspecifics.general.companycar",
    step: 13,
    type: "object",
    possibleValues: "{ status: 'true'|'false'|'', carcount: number|null }",
  },
  {
    key: "industryspecifics.general.cashregister",
    step: 13,
    type: "object",
    possibleValues:
      "{ status: 'true'|'false'|'', cashregistercount: number|null }",
  },
  {
    key: "industryspecifics.general.multipleaccounts",
    step: 13,
    type: "object",
    possibleValues: "{ status: 'true'|'false'|'', accountcount: number|null }",
  },
  {
    key: "industryspecifics.general.customertype.selectedoption",
    step: 13,
    type: "string",
    possibleValues:
      "'private' | 'mainlyprivate' | 'mixed' | 'business' | 'idk'",
  },

  // Step 15 - languages.jsx
  {
    key: "languagefilter",
    step: 15,
    type: "boolean",
    recommended: true,
    possibleValues: "true / false",
  },
  {
    key: "languages",
    step: 15,
    type: "object",
    recommended: true,
    possibleValues:
      "{ hungarian, english, german, french, chinese, russian, romanian, spanish: boolean }",
  },

  // Step 16 - preferences.jsx (match_score_weights is a separate redux slice)
  {
    key: "zip_code",
    step: 16,
    type: "number|string",
    recommended: true,
    possibleValues: "4 számjegy (csak ha distanceWeight === 5)",
  },
  {
    key: "spSelectedFromList",
    step: 16,
    type: "string",
    possibleValues: "string",
  },

  // Step 17 - flowswitch.jsx
  {
    key: "email",
    step: 17,
    type: "string",
    required: true,
    possibleValues: "érvényes email",
  },
  {
    key: "flowswitchchoice",
    step: 17,
    type: "string",
    required: true,
    possibleValues: "'asp' | 'cherry'",
  },
  {
    key: "isfakecherry",
    step: 17,
    type: "boolean",
    recommended: true,
    possibleValues: "true / false",
  },

  // Step 18 - aspProviders.jsx
  {
    key: "selected_providers",
    step: 18,
    type: "array",
    required: true,
    possibleValues: "kiválasztott szolgáltatók tömbje",
  },
  {
    key: "discountCode",
    step: 18,
    type: "string",
    possibleValues: "kupon kód string",
  },
  {
    key: "isDiscounted",
    step: 18,
    type: "boolean",
    possibleValues: "true / false",
  },
  {
    key: "discount",
    step: 18,
    type: "object",
    possibleValues: "kupon adatai (API)",
  },
  {
    key: "potential_providers",
    step: 18,
    type: "array",
    possibleValues: "API-ról jövő tömb",
  },
];

const MATCH_SCORE_WEIGHT_KEYS = [
  "distanceWeight",
  "experienceWeight",
  "reviewWeight",
  "liabilityInsWeight",
  "priceWeight",
  "badgeWeight",
  "phoneWeight",
  "availabilityWeight",
];

/**
 * Meta variables populated automatically by SearchFlowVersionB (componentDidMount
 * and flow control); not entered on individual steps. They should always have
 * values — useful feedback for what happened in the background.
 */
const META_VARIABLES = [
  {
    key: "abVersion",
    type: "string",
    required: true,
    possibleValues: "'A' | 'B'",
  },
  {
    key: "searchID",
    type: "string",
    required: true,
    possibleValues: "backend által kiadott azonosító",
  },
  { key: "searchUser", type: "string", possibleValues: "bookkeepie_id (uuid)" },
  {
    key: "activeStep",
    type: "number",
    possibleValues: "aktuális lépés indexe",
  },
  {
    key: "sourceUrl",
    type: "string",
    possibleValues: "document.referrer (ha van)",
  },
  { key: "utm_source", type: "string", possibleValues: "UTM source" },
  { key: "utm_medium", type: "string", possibleValues: "UTM medium" },
  { key: "utm_campaign", type: "string", possibleValues: "UTM campaign" },
  { key: "utm_content", type: "string", possibleValues: "UTM content" },
  { key: "utm_term", type: "string", possibleValues: "UTM term" },
  { key: "gclid", type: "string", possibleValues: "Google click id" },
  { key: "dclid", type: "string", possibleValues: "Display click id" },
  {
    key: "cegjelzoData",
    type: "object",
    possibleValues: "Cégjelzőből választott cég objektum (vagy null)",
  },
  {
    key: "spSelectedFromList",
    type: "string",
    possibleValues: "localStorage-ból, ha van",
  },
];

// Helper: read a dot-path from an object
const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc, key) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[key];
  }, obj);
};

// Helper: decide if a primitive value is "user-provided" (not just a default
// initial value from the reducer). false/0 are treated as NOT filled because the
// reducer pre-populates the entire industryspecifics tree with `false`, `null`,
// `''`, `0` defaults. We only consider real user input as filled.
const isPrimitiveFilled = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "number") return value !== 0;
  if (typeof value === "boolean") return value === true;
  return false;
};

// ASP / cherry: potential_providers and selected_providers are often [[tier1],[tier2],[tier3]].
// With empty slots the outer array can still have length > 0 — do not treat that as "filled".
const aspGroupedProviderSlots = (arr) =>
  Array.isArray(arr) &&
  arr.length > 0 &&
  arr.every((x) => Array.isArray(x));

const aspGroupedHasAnyProvider = (arr) =>
  aspGroupedProviderSlots(arr) && arr.some((inner) => inner.length > 0);

/** True when there is a real selection or offer: non-empty flat array, or grouped shape with at least one non-empty inner array. */
const providerArrayHasOffers = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  if (aspGroupedProviderSlots(arr)) return aspGroupedHasAnyProvider(arr);
  return true;
};

/**
 * The matcher writes into potential_providers in Redux; selected_providers often
 * stays empty until the user explicitly selects on the ASP step. For the preview
 * "ASP-ready" signal: treat as filled if there is a selected provider OR the matcher
 * has already returned offers in potential_providers.
 */
const isSelectedProvidersFilled = (value, rootStore) => {
  if (providerArrayHasOffers(value)) return true;
  return providerArrayHasOffers(rootStore?.potential_providers);
};

// Helper: decide if a value counts as "filled"
// fieldKey: top-level SEARCH_VARIABLES / META key only (nested recursion omits it)
// rootStore: company_store — only needed for the selected_providers row
const isFilled = (value, fieldKey, rootStore) => {
  if (fieldKey === "taxform" && value === "") return true;
  if (fieldKey === "selected_providers" && rootStore) {
    return isSelectedProvidersFilled(value, rootStore);
  }
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    // object: filled if any leaf value is filled (recursive)
    return Object.values(value).some((v) => isFilled(v));
  }
  return isPrimitiveFilled(value);
};

const formatValue = (value) => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "string") return value === "" ? "—" : value;
  if (typeof value === "number") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Mapping: which Cégjelző (company registry) field could feed which of our search
 * variables. Multiple cegjelzoKeys are allowed (aliases) — we take the first match.
 * `mapsTo` is a visual hint for developers/PMs only; it does NOT perform copying.
 *
 * Cégjelző `type` enum (see CegjelzoSearch.jsx normalizeCompanyType):
 *   0/COMPANIES         → bt | kft | zrt | kiva (parseable from company name suffix)
 *   1/SELF_EMPLOYED     → singleprimary | singlesecondary | vszja | entrepreneur
 *                          (side/main job would need a separate field)
 *   2/CIVIL_ORGS        → not covered by our enum
 *   3/BUDGET_ORGS       → not covered by our enum
 *   4/GROUP_TAX_NUMBERS → from tax-number search, not company form
 */
const CEGJELZO_FIELD_MAP = [
  {
    cegjelzoKeys: ["name"],
    label: i18n.t("general.companyname"),
    mapsTo: [
      { var: "companyctcname", note: "közvetlenül" },
      {
        var: "companytype",
        note: "suffix parse: 'Kft.'→kft, 'Bt.'→bt, 'Zrt.'→zrt, 'Nyrt.'→zrt",
      },
    ],
  },
  {
    cegjelzoKeys: ["short_name"],
    label: i18n.t("searchflow.labelshortname"),
    mapsTo: [{ var: "companyctcname", note: "fallback, ha name túl hosszú" }],
  },
  {
    cegjelzoKeys: ["type"],
    label: i18n.t("searchflow.labeltypeenum"),
    mapsTo: [
      {
        var: "companytype",
        note: "COMPANIES → kft/bt/zrt/kiva (név alapján); SELF_EMPLOYED → entrepreneur (default); CIVIL_ORGS/BUDGET_ORGS → nincs lefedve",
      },
    ],
  },
  {
    cegjelzoKeys: ["full_tax_number", "tax_number"],
    label: i18n.t("general.taxnumber"),
    mapsTo: [
      { var: "vatFilter", note: "9. számjegy: 1=alanyi mentes, 2=ÁFA-s" },
      { var: "vatPayment", note: "9. számjegyből részben kikövetkeztethető" },
    ],
  },
  {
    cegjelzoKeys: ["eu_tax_number", "vat_eu_number", "community_tax_number"],
    label: i18n.t("searchflow.labelcommunitytax"),
    mapsTo: [
      { var: "foreign_trade", note: "ha létezik → valószínűleg true" },
      { var: "foreigntradeEU", note: "ha létezik → 'true'" },
    ],
  },
  {
    cegjelzoKeys: ["main_activity", "main_teaor", "teaor"],
    label: i18n.t("searchflow.labelmainactivity"),
    mapsTo: [
      { var: "industry", note: "TEÁOR → industry enum mapping táblázat kell" },
    ],
  },
  {
    cegjelzoKeys: ["address", "registered_address", "headquarters"],
    label: i18n.t("searchflow.labeladdress"),
    mapsTo: [{ var: "zip_code", note: "irányítószám parse-olható a címből" }],
  },
  {
    cegjelzoKeys: ["zip_code", "postal_code"],
    label: i18n.t("general.zip"),
    mapsTo: [{ var: "zip_code", note: "közvetlenül" }],
  },
  {
    cegjelzoKeys: ["num_of_employees", "employee_count", "headcount"],
    label: i18n.t("general.noofemployees"),
    mapsTo: [
      { var: "noofemployees", note: "közvetlenül (5 fölött '5+'-ra váltás)" },
      { var: "payroll", note: "ha > 0 → true" },
    ],
  },
  {
    cegjelzoKeys: ["foundation_date", "founding_date", "registered_at"],
    label: i18n.t("searchflow.labelfoundingdate"),
    mapsTo: [{ var: "newlyfounded", note: "ha < 1 éve → true" }],
  },
  {
    cegjelzoKeys: ["vat_status"],
    label: i18n.t("searchflow.vattitle"),
    mapsTo: [
      { var: "vatFilter" },
      { var: "vatPayment", note: "ha tartalmazza a periodicitást" },
    ],
  },
  {
    cegjelzoKeys: ["status", "company_status"],
    label: i18n.t("searchflow.labelcompanystatus"),
    mapsTo: [
      {
        var: "answer",
        note: "aktív → 'existing'; törölt esetén figyelmeztetés",
      },
    ],
  },
];

// Pick the first defined value among aliases
const pickCegjelzoValue = (data, keys) => {
  if (!data) return undefined;
  for (const k of keys) {
    if (data[k] !== undefined && data[k] !== null && data[k] !== "") {
      return { key: k, value: data[k] };
    }
  }
  return undefined;
};

const VarRow = ({ entry, value, filled }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.25,
      py: 0.75,
      borderBottom: "1px solid #eee",
    }}
  >
    <Box
      sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
    >
      <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
        {entry.key}
      </Typography>
      <Chip
        label={entry.type}
        size="small"
        variant="outlined"
        sx={{ height: 18, fontSize: "10px" }}
      />
      {entry.required && (
        <Chip
          label={i18n.t("searchflow.sdpobligatory")}
          size="small"
          color="error"
          sx={{ height: 18, fontSize: "10px" }}
        />
      )}
      {entry.recommended && !entry.required && (
        <Chip
          label={i18n.t("searchflow.sdprecommended")}
          size="small"
          color="warning"
          sx={{ height: 18, fontSize: "10px" }}
        />
      )}
      {filled ? (
        <Chip
          label={i18n.t("searchflow.sdpfilled")}
          size="small"
          color="success"
          sx={{ height: 18, fontSize: "10px" }}
        />
      ) : (
        <Chip
          label={i18n.t("searchflow.sdpmissing")}
          size="small"
          color="warning"
          sx={{ height: 18, fontSize: "10px" }}
        />
      )}
    </Box>
    <Typography sx={{ fontSize: "11px", color: "#666", fontStyle: "italic" }}>
      {i18n.t("searchflow.sdppossiblevalues")} {entry.possibleValues}
    </Typography>
    {filled && (
      <Box
        component="pre"
        sx={{
          margin: 0,
          fontSize: "12px",
          fontFamily: "monospace",
          background: "#f6f8fa",
          padding: "4px 8px",
          borderRadius: "4px",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {formatValue(value)}
      </Box>
    )}
  </Box>
);

const SearchDataPreview = () => {
  const companyStore = useSelector((state) => state.company_store) || {};
  const matchScoreWeights =
    useSelector((state) => state.match_score_weights) || {};

  // Evaluate each variable
  const evaluated = SEARCH_VARIABLES.map((entry, index) => {
    const value = getValueByPath(companyStore, entry.key);
    return {
      entry,
      value,
      filled: isFilled(value, entry.key, companyStore),
      rowKey: `search-${entry.key}-${index}`,
    };
  });

  // Meta variables populated by the flow itself (abVersion, searchID, UTMs, etc.)
  const metaEvaluated = META_VARIABLES.map((entry, index) => {
    const value = getValueByPath(companyStore, entry.key);
    return {
      entry,
      value,
      filled: isFilled(value, entry.key, companyStore),
      rowKey: `meta-${entry.key}-${index}`,
    };
  });

  // Add match_score_weights as separate "weights" entries (step 16)
  const weightEvaluated = MATCH_SCORE_WEIGHT_KEYS.map((k, index) => {
    const value = matchScoreWeights[k];
    return {
      entry: {
        key: `match_score_weights.${k}`,
        step: 16,
        type: "number",
        possibleValues: "0 vagy 5",
      },
      value,
      filled: value !== undefined && value !== null,
      rowKey: `weight-${k}-${index}`,
    };
  });

  const all = [...evaluated, ...weightEvaluated, ...metaEvaluated];
  const filled = all.filter((e) => e.filled);
  const missing = all.filter((e) => !e.filled);

  // Required-for-ASP-request subset
  const requiredAll = all.filter((e) => e.entry.required);
  const requiredFilled = requiredAll.filter((e) => e.filled);
  const requiredMissing = requiredAll.filter((e) => !e.filled);

  // Business-recommended subset (not strictly required but worth filling for
  // a usable ASP request). Required fields are excluded to avoid duplication.
  const recommendedAll = all.filter(
    (e) => e.entry.recommended && !e.entry.required,
  );
  const recommendedFilled = recommendedAll.filter((e) => e.filled);
  const recommendedMissing = recommendedAll.filter((e) => !e.filled);

  const cegjelzoData = companyStore.cegjelzoData;

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
      <Typography sx={{ fontSize: "16px", fontWeight: 700, mb: 1 }}>
        {i18n.t("searchflow.sdpintro1")}{" "}
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#666", mb: 2 }}>
        {i18n.t("searchflow.sdpintro2")}
      </Typography>

      {/* Cegjelzo section */}
      <Accordion sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="div" sx={{ fontWeight: 700 }}>
            {i18n.t("searchflow.sdpcegjelzotitle")}{" "}
            <Chip
              label={cegjelzoData ? i18n.t("searchflow.sdploaded") : i18n.t("searchflow.sdpnone")}
              size="small"
              color={cegjelzoData ? "success" : "default"}
              sx={{
                ml: 1,
                height: 18,
                "& .MuiChip-label": { fontSize: "10px", px: 0.75 },
              }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {cegjelzoData ? (
            <Box>
              <Typography sx={{ fontSize: "12px", color: "#666", mb: 1.5 }}>
                {i18n.t("searchflow.sdpcegjelzohint")}
              </Typography>

              {CEGJELZO_FIELD_MAP.map((entry) => {
                const found = pickCegjelzoValue(
                  cegjelzoData,
                  entry.cegjelzoKeys,
                );
                return (
                  <Box
                    key={entry.label}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      py: 0.75,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
                        {entry.label}
                      </Typography>
                      <Chip
                        label={
                          found ? found.key : entry.cegjelzoKeys.join(" / ")
                        }
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "10px" }}
                      />
                      <Chip
                        label={found ? i18n.t("searchflow.sdpavailable") : i18n.t("searchflow.sdpnotinpayload")}
                        size="small"
                        color={found ? "success" : "default"}
                        sx={{ height: 18, fontSize: "10px" }}
                      />
                    </Box>

                    {found && (
                      <Box
                        component="pre"
                        sx={{
                          margin: 0,
                          fontSize: "12px",
                          fontFamily: "monospace",
                          background: "#f6f8fa",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {formatValue(found.value)}
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mt: 0.25,
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "11px", color: "#666", mr: 0.5 }}
                      >
                        {i18n.t("searchflow.sdpmapsto")}
                      </Typography>
                      {entry.mapsTo.map((m) => (
                        <Chip
                          key={`${entry.label}-${m.var}`}
                          label={m.note ? `${m.var} (${m.note})` : m.var}
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ height: 18, fontSize: "10px" }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}

              {(() => {
                const mappedKeys = new Set(
                  CEGJELZO_FIELD_MAP.flatMap((e) => e.cegjelzoKeys),
                );
                const extras = Object.entries(cegjelzoData).filter(
                  ([k]) => !mappedKeys.has(k),
                );
                if (extras.length === 0) return null;
                return (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#666",
                        mb: 0.5,
                      }}
                    >
                      {i18n.t("searchflow.sdpextrafields")}
                    </Typography>
                    <Box
                      component="pre"
                      sx={{
                        margin: 0,
                        fontSize: "11px",
                        fontFamily: "monospace",
                        background: "#f6f8fa",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 240,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(Object.fromEntries(extras), null, 2)}
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          ) : (
            <Typography sx={{ fontSize: "13px", color: "#888" }}>
              {i18n.t("searchflow.sdpnocompany")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />

      {/* Required for ASP request */}
      <Accordion
        sx={{
          mb: 1,
          border: "1px solid #ffcdd2",
          background: "#fff5f5",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="div" sx={{ fontWeight: 700, color: "#c62828" }}>
            {i18n.t("searchflow.sdprequiredtitle")}{" "}
            <Chip
              label={`${requiredFilled.length}/${requiredAll.length}`}
              size="small"
              color={requiredMissing.length === 0 ? "success" : "error"}
              sx={{
                ml: 1,
                height: 18,
                "& .MuiChip-label": { fontSize: "10px", px: 0.75 },
              }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {requiredAll.map((e) => (
            <VarRow
              key={`req-${e.rowKey}`}
              entry={e.entry}
              value={e.value}
              filled={e.filled}
            />
          ))}
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />

      {/* Business-recommended for a usable ASP request */}
      <Accordion
        sx={{
          mb: 1,
          border: "1px solid #ffe0b2",
          background: "#fffaf2",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="div" sx={{ fontWeight: 700, color: "#e65100" }}>
            {i18n.t("searchflow.sdprecommendedtitle")}{" "}
            <Chip
              label={`${recommendedFilled.length}/${recommendedAll.length}`}
              size="small"
              color={recommendedMissing.length === 0 ? "success" : "warning"}
              sx={{
                ml: 1,
                height: 18,
                "& .MuiChip-label": { fontSize: "10px", px: 0.75 },
              }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography sx={{ fontSize: "12px", color: "#666", mb: 1 }}>
            {i18n.t("searchflow.sdprecommendedhint")}
          </Typography>
          {recommendedAll.map((e) => (
            <VarRow
              key={`rec-${e.rowKey}`}
              entry={e.entry}
              value={e.value}
              filled={e.filled}
            />
          ))}
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 1 }} />

      {/* Filled fields */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="div" sx={{ fontWeight: 700 }}>
            {i18n.t("searchflow.sdpfilledtitle")}{" "}
            <Chip
              label={filled.length}
              size="small"
              color="success"
              sx={{
                ml: 1,
                height: 18,
                "& .MuiChip-label": { fontSize: "10px", px: 0.75 },
              }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {filled.length === 0 ? (
            <Typography sx={{ fontSize: "13px", color: "#888" }}>
              {i18n.t("searchflow.sdpnofilled")}
            </Typography>
          ) : (
            filled.map((e) => (
              <VarRow
                key={`filled-${e.rowKey}`}
                entry={e.entry}
                value={e.value}
                filled
              />
            ))
          )}
        </AccordionDetails>
      </Accordion>

      {/* Missing fields */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography component="div" sx={{ fontWeight: 700 }}>
            {i18n.t("searchflow.sdpmissingtitle")}{" "}
            <Chip
              label={missing.length}
              size="small"
              color="warning"
              sx={{
                ml: 1,
                height: 18,
                "& .MuiChip-label": { fontSize: "10px", px: 0.75 },
              }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {missing.length === 0 ? (
            <Typography sx={{ fontSize: "13px", color: "#888" }}>
              {i18n.t("searchflow.sdpallfilled")}
            </Typography>
          ) : (
            missing.map((e) => (
              <VarRow
                key={`missing-${e.rowKey}`}
                entry={e.entry}
                value={e.value}
                filled={false}
              />
            ))
          )}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default SearchDataPreview;
